import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import { CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES, lotteryService, type BetSlip } from '../../services/lotteryService'
import * as walletService from '../../services/walletService'
import { filterSlipsForTab } from '@huay/shared/slipTabFilters'
import { getSlipStatusLabel, isRoundCancellationSlip } from '@huay/shared/slipDisplayStatus'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'
import './ModernLobbyHuayForms.css'

const PAGE_SIZE = 20

const SLIP_TABS = [
  { id: 'wait', label: 'รอผล', icon: 'far fa-clock', filter: 'waiting' as const },
  { id: 'done', label: 'ผลออกแล้ว', icon: 'far fa-check-circle', filter: 'checked' as const },
  { id: 'today', label: 'โพยวันนี้', icon: 'far fa-sun', filter: 'today' as const },
  { id: 'history', label: 'ดูย้อนหลัง', icon: 'fas fa-calendar-alt', filter: 'all' as const },
]

const statusLabel: Record<string, string> = {
  pending: 'รอผล',
  confirmed: 'รอผล',
  won: 'ถูกรางวัล',
  lost: 'ไม่ถูกรางวัล',
  partial_win: 'ถูกรางวัล',
  cancelled: 'ยกเลิก',
  refunded: 'คืนเงิน',
}

function statusTone(slip: BetSlip): string {
  const status = String(slip.status || 'pending').toLowerCase()
  if (isRoundCancellationSlip(slip)) return 'is-cancelled'
  if (status === 'won' || status === 'partial_win') return 'is-won'
  if (status === 'lost') return 'is-lost'
  if (status === 'cancelled' || status === 'refunded') return 'is-cancelled'
  if (status === 'pending' || status === 'confirmed' || status === 'waiting') return 'is-pending'
  return 'is-pending'
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr)
    return d.toLocaleString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

function isToday(dateStr: string): boolean {
  try {
    const d = new Date(dateStr)
    const now = new Date()
    return (
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear()
    )
  } catch {
    return false
  }
}

function getUserId(): string | null {
  const token = localStorage.getItem('accessToken')
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload.sub ?? payload.id ?? payload.userId ?? null
    } catch {
      // ignore
    }
  }
  try {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      const u = JSON.parse(userStr) as { id?: string; _id?: string }
      return u?.id ?? u?._id ?? null
    }
  } catch {
    // ignore
  }
  return null
}

function getCustomerCancelWindowMinutes(slip: BetSlip): number {
  const configured = Number(slip.customerCancelWindowMinutes)
  return Number.isFinite(configured) && configured > 0
    ? configured
    : CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES
}

function canCustomerCancelSlip(slip: BetSlip): boolean {
  const status = String(slip.status || '').toLowerCase()
  if (slip.isSettled || ['cancelled', 'refunded'].includes(status)) return false

  const createdAt = slip.createdAt ? new Date(slip.createdAt).getTime() : NaN
  if (!Number.isFinite(createdAt)) return false

  return Date.now() - createdAt <= getCustomerCancelWindowMinutes(slip) * 60 * 1000
}

function getSlipSummaryDisplayAmount(slip: BetSlip): number | null {
  if (!slip.isSettled) return null

  const status = String(slip.status || '').toLowerCase()
  if (status === 'lost') return 0

  if (status === 'won' || status === 'partial_win') {
    const totalWin = Number(slip.totalWinAmount)
    return Number.isFinite(totalWin) ? totalWin : 0
  }

  return null
}

/** หน้า โพยหวย — ดึง API แบบ user-classic TicketAllPage, UI แบบ modern */
const ModernLobbyHuaySlipPage: React.FC = () => {
  useOutletContext<LobbyHuayOutletContext>()
  const navigate = useNavigate()
  const [activeTabId, setActiveTabId] = useState('wait')
  const [slips, setSlips] = useState<BetSlip[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  /** ค้นหาโพย — กรองตามรหัสโพย, ชื่อหวย, รอบ (ใช้ในทุกแท็บ) */
  const [searchQuery, setSearchQuery] = useState('')

  const currentTab = SLIP_TABS.find((t) => t.id === activeTabId) ?? SLIP_TABS[0]

  const loadSlips = useCallback(
    async (pageNum: number = 1) => {
      const userId = getUserId()
      if (!userId) {
        setSlips([])
        setTotal(0)
        setLoading(false)
        setError('กรุณาเข้าสู่ระบบ')
        return
      }
      try {
        setLoading(true)
        setError(null)
        const skip = (pageNum - 1) * PAGE_SIZE
        const isSettled =
          currentTab.filter === 'waiting'
            ? false
            : currentTab.filter === 'checked'
              ? true
              : undefined

        const { slips: data, total: totalCount } = await lotteryService.getUserSlips(userId, {
          limit: currentTab.filter === 'today' ? 200 : PAGE_SIZE,
          skip: currentTab.filter === 'today' ? 0 : skip,
          ...(isSettled !== undefined && { isSettled }),
        })

        if (currentTab.filter === 'today') {
          const todaySlips = data.filter((s) => s.createdAt && isToday(s.createdAt))
          setSlips(todaySlips)
          setTotal(todaySlips.length)
          setPage(1)
        } else {
          setSlips(data)
          setTotal(totalCount)
          setPage(pageNum)
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'โหลดโพยไม่สำเร็จ'
        setError(msg)
        setSlips([])
        setTotal(0)
      } finally {
        setLoading(false)
      }
    },
    [currentTab.filter]
  )

  useEffect(() => {
    loadSlips(1)
  }, [loadSlips])

  const filtered = useMemo(
    () => filterSlipsForTab(slips, currentTab.filter),
    [slips, currentTab.filter]
  )

  /** กรองด้วยคำค้น — รหัสโพย, ชื่อหวย, รอบ, วันที่ (ใช้ได้ทุกแท็บ) */
  const searchFiltered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return filtered
    return filtered.filter((s) => {
      const slipId = String(s.slipId ?? s._id ?? '').toLowerCase()
      const huayName = String(s.huayName ?? '').toLowerCase()
      const roundNumber = String(s.roundNumber ?? '').toLowerCase()
      const createdAt = s.createdAt ? formatDate(s.createdAt).toLowerCase() : ''
      return (
        slipId.includes(q) ||
        huayName.includes(q) ||
        roundNumber.includes(q) ||
        createdAt.includes(q)
      )
    })
  }, [filtered, searchQuery])

  const totalPages =
    currentTab.filter === 'today' ? 1 : Math.max(1, Math.ceil(total / PAGE_SIZE))
  const hasPrev = page > 1
  const hasNext = page < totalPages

  const goToPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return
    loadSlips(nextPage)
  }

  const handleViewDetail = (slipId: string) => {
    navigate(`/lobby-huay/slip/detail/${slipId}`)
  }

  const handleCancelSlip = async (slip: BetSlip) => {
    const cancelWindowMinutes = getCustomerCancelWindowMinutes(slip)
    if (!canCustomerCancelSlip(slip)) {
      await Swal.fire({
        icon: 'warning',
        title: 'ไม่สามารถคืนโพยได้',
        text: `คืนโพยได้ภายใน ${cancelWindowMinutes} นาทีหลังจากที่แทง และต้องยังไม่ปิดรับแทง`,
      })
      return
    }

    const result = await Swal.fire({
      icon: 'warning',
      title: 'ยืนยันการคืนโพย',
      html: `คุณต้องการคืนโพย <strong>${slip.slipId ?? slip._id ?? '-'}</strong> ใช่หรือไม่?<br/><small>ระบบจะคืนยอดสุทธิของโพยกลับเข้ากระเป๋า</small>`,
      showCancelButton: true,
      confirmButtonText: 'คืนโพย',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#f59e0b',
    })

    if (!result.isConfirmed) return

    const response = await lotteryService.cancelSlip(String(slip.slipId ?? slip._id ?? ''))
    if (!response.success) {
      await Swal.fire({
        icon: 'error',
        title: 'คืนโพยไม่สำเร็จ',
        text: response.message || 'กรุณาลองใหม่อีกครั้ง',
      })
      return
    }

    await Swal.fire({
      icon: 'success',
      title: 'คืนโพยสำเร็จ',
      text: response.message || 'ระบบคืนโพยเรียบร้อยแล้ว',
    })

    await loadSlips(currentTab.filter === 'today' ? 1 : page)
  }

  const tabNote =
    currentTab.filter === 'waiting'
      ? 'โพยในแท็บนี้จะย้ายไป "ผลออกแล้ว" เมื่อมีการประกาศผลแพ้/ชนะ'
      : currentTab.filter === 'checked'
        ? 'โพยที่ประกาศผลแพ้/ชนะแล้ว'
        : null

  return (
    <div className="htd-slip">
      {tabNote ? (
        <p className="htd-slip__note">
          <i className="fas fa-info-circle me-1" aria-hidden />
          {tabNote}
        </p>
      ) : null}

      <div className="htd-slip__tabs" role="tablist" aria-label="หมวดโพยหวย">
        {SLIP_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTabId === tab.id}
            className={`htd-slip__tab${activeTabId === tab.id ? ' is-active' : ''}`}
            onClick={() => setActiveTabId(tab.id)}
          >
            <i className={tab.icon} aria-hidden />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="htd-slip__search">
        <i className="fas fa-search" aria-hidden />
        <input
          type="text"
          placeholder="ค้นหารหัสโพย, ชื่อหวย, รอบ, วันที่..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="ค้นหาโพย"
        />
        {searchQuery.trim() ? (
          <button
            type="button"
            className="htd-slip__search-clear"
            onClick={() => setSearchQuery('')}
            aria-label="ล้างคำค้น"
          >
            <i className="fas fa-times" />
          </button>
        ) : null}
      </div>
      <p className="htd-slip__meta">
        {searchQuery.trim()
          ? `แสดง ${searchFiltered.length} รายการ (จาก ${filtered.length} รายการในแท็บนี้)`
          : totalPages > 1
            ? `โหลด ${filtered.length} รายการ จากทั้งหมด ${total} รายการ (${PAGE_SIZE} รายการต่อหน้า)`
            : `โหลด ${filtered.length} รายการ`}
      </p>

      {loading && slips.length === 0 ? (
        <div className="htd-slip__state" aria-busy="true">
          <i className="fas fa-spinner fa-spin" style={{ color: '#7dd3fc', fontSize: '1.4rem' }} />
          <p>กำลังโหลดโพย...</p>
        </div>
      ) : null}

      {error && slips.length === 0 && !loading ? (
        <div className="htd-slip__state htd-slip__state--error" role="alert">
          <p>{error === 'Failed to fetch' ? 'โหลดโพยไม่สำเร็จ กรุณาลองใหม่อีกครั้ง' : error}</p>
          <button type="button" className="htd-form__btn htd-form__btn--primary" style={{ marginTop: '0.75rem' }} onClick={() => loadSlips(1)}>
            <i className="fas fa-redo" aria-hidden />
            โหลดใหม่
          </button>
        </div>
      ) : null}

      {!loading || slips.length > 0 ? (
        <div className="htd-slip__list">
          {searchFiltered.length === 0 && !error ? (
            <div className="htd-slip__state">
              <p>
                {filtered.length === 0
                  ? 'ไม่มีข้อมูลโพยในแท็บนี้'
                  : 'ไม่พบโพยที่ตรงกับคำค้น ลองเปลี่ยนคำค้นหรือล้างการค้นหา'}
              </p>
            </div>
          ) : (
            searchFiltered.map((slip, index) => {
              const status = (slip.status || 'pending') as string
              const summaryAmount = getSlipSummaryDisplayAmount(slip)
              const displayOrdinal = searchQuery.trim()
                ? index + 1
                : currentTab.filter === 'today'
                  ? index + 1
                  : (page - 1) * PAGE_SIZE + index + 1
              return (
                <article
                  key={slip._id}
                  className={`htd-slip__card${statusTone(slip) === 'is-pending' ? ' is-pending' : ''}`}
                  style={{ animationDelay: `${Math.min(index, 12) * 0.04}s` }}
                >
                  <div className="htd-slip__card-top">
                    <h6>โพยที่ {displayOrdinal}</h6>
                    <div className="htd-slip__badges">
                      {canCustomerCancelSlip(slip) ? (
                        <button
                          type="button"
                          className="htd-slip__cancel-btn"
                          onClick={() => void handleCancelSlip(slip)}
                        >
                          คืนโพย
                        </button>
                      ) : null}
                      <span className={`htd-slip__status ${statusTone(slip)}`}>
                        {getSlipStatusLabel(slip, statusLabel, status)}
                      </span>
                    </div>
                  </div>

                  <div className="htd-slip__code">
                    <i className="fas fa-clipboard-list" aria-hidden />
                    รหัสโพย {slip.slipId ?? slip._id ?? '-'}
                  </div>

                  <div className="htd-slip__names">
                    <strong>{slip.huayName ?? '-'}</strong>
                    <span>รอบที่ {slip.roundNumber ?? '-'}</span>
                  </div>

                  <div className="htd-slip__metrics">
                    <div className="htd-slip__metric">
                      <small>เงินเดิมพันทั้งหมด</small>
                      <strong>{walletService.formatCurrency(slip.totalAmount ?? 0)}</strong>
                    </div>
                    <div className="htd-slip__metric">
                      <small>สรุปผลแพ้/ชนะ</small>
                      <strong>
                        {summaryAmount != null ? walletService.formatCurrency(summaryAmount) : '-'}
                      </strong>
                    </div>
                  </div>

                  <div className="htd-slip__card-foot">
                    <time>
                      <i className="fas fa-clock me-1" aria-hidden />
                      {slip.createdAt ? formatDate(slip.createdAt) : '-'}
                    </time>
                    <button
                      type="button"
                      className="htd-slip__detail-btn"
                      onClick={() => handleViewDetail(String(slip.slipId ?? slip._id))}
                    >
                      <i className="fas fa-search me-1" aria-hidden />
                      รายละเอียด
                    </button>
                  </div>
                </article>
              )
            })
          )}
        </div>
      ) : null}

      {filtered.length > 0 && !searchQuery.trim() ? (
        <div className="htd-slip__pager">
          <small>
            โพยที่ {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)} จาก {total}
          </small>
          <div className="htd-slip__pager-btns">
            <button type="button" disabled={!hasPrev} onClick={() => goToPage(page - 1)}>
              ก่อนหน้า
            </button>
            <span>
              หน้า {page} / {totalPages}
            </span>
            <button type="button" disabled={!hasNext} onClick={() => goToPage(page + 1)}>
              ถัดไป
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default ModernLobbyHuaySlipPage
